import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { auditApi } from '../services/api';
import { useProject } from '../App';
import { formatTimestamp } from '../utils/format';
import AutocompleteInput from '../components/AutocompleteInput';

function TransactionList() {
    const { selectedProject } = useProject();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [filters, setFilters] = useState({
        source: '',
        functionName: '',
        userId: '',
        userName: '',
        tableName: '',
        startDate: '',
        endDate: '',
    });

    const loadTransactions = useCallback(async (searchPage, overrideFilters) => {
        try {
            setLoading(true);
            const currentPage = searchPage !== undefined ? searchPage : page;
            if (searchPage !== undefined) setPage(searchPage);
            const activeFilters = overrideFilters || filters;
            const params = { page: currentPage, size: 20 };
            if (selectedProject) params.projectId = selectedProject;
            if (activeFilters.source) params.source = activeFilters.source;
            if (activeFilters.functionName) params.functionName = activeFilters.functionName;
            if (activeFilters.userId) params.userId = activeFilters.userId;
            if (activeFilters.userName) params.userName = activeFilters.userName;
            if (activeFilters.tableName) params.tableName = activeFilters.tableName;
            if (activeFilters.startDate) params.startDate = activeFilters.startDate + 'T00:00:00+08:00';
            if (activeFilters.endDate) params.endDate = activeFilters.endDate + 'T23:59:59+08:00';

            const res = await auditApi.queryTransactions(params);
            setTransactions(res.data.content || []);
            setTotalPages(res.data.totalPages || 0);
        } catch (err) {
            console.error('Failed to load transactions:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedProject, page, filters]);

    useEffect(() => {
        loadTransactions();
    }, [loadTransactions]);

    const handleSearch = () => {
        setPage(0);
        loadTransactions(0);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const setDateRange = (preset) => {
        const today = new Date();
        const end = today.toISOString().split('T')[0];
        let start;
        switch (preset) {
            case 'today':
                start = end;
                break;
            case '7days':
                start = new Date(today.getTime() - 7 * 86400000).toISOString().split('T')[0];
                break;
            case '30days':
                start = new Date(today.getTime() - 30 * 86400000).toISOString().split('T')[0];
                break;
            default:
                return;
        }
        const newFilters = { ...filters, startDate: start, endDate: end };
        setFilters(newFilters);
        setPage(0);
        loadTransactions(0, newFilters);
    };

    const clearDateRange = () => {
        const newFilters = { ...filters, startDate: '', endDate: '' };
        setFilters(newFilters);
        loadTransactions(0, newFilters);
    };

    const hasAnyFilter = filters.source || filters.functionName || filters.userId ||
        filters.userName || filters.tableName || filters.startDate || filters.endDate;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-gray-900">Transaction History</h1>
                {hasAnyFilter && (
                    <button
                        onClick={() => {
                            setFilters({ source: '', functionName: '', userId: '', userName: '', tableName: '', startDate: '', endDate: '' });
                            setPage(0);
                            loadTransactions(0);
                        }}
                        className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                    >
                        Clear All Filters
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Filters</h2>
                <div className="space-y-3">
                    {/* Row 1: Text filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
                            <select
                                value={filters.source}
                                onChange={(e) => handleFilterChange('source', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                            >
                                <option value="">All Sources</option>
                                <option value="GUI">GUI</option>
                                <option value="SQL_DIRECT">SQL Direct</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Function Name</label>
                            <input
                                type="text"
                                value={filters.functionName}
                                onChange={(e) => handleFilterChange('functionName', e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="e.g., updateOrder"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">User Name</label>
                            <input
                                type="text"
                                value={filters.userName}
                                onChange={(e) => handleFilterChange('userName', e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="e.g., 张三"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">User ID</label>
                            <AutocompleteInput
                                value={filters.userId}
                                onChange={(val) => handleFilterChange('userId', val)}
                                onKeyDown={handleKeyDown}
                                placeholder="e.g., operator_zhang"
                                fetchSuggestions={(q) => auditApi.suggestUserIds(q, 10)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Table Name</label>
                            <AutocompleteInput
                                value={filters.tableName}
                                onChange={(val) => handleFilterChange('tableName', val)}
                                onKeyDown={handleKeyDown}
                                placeholder="e.g., CA_DOMAIN_LANG"
                                fetchSuggestions={(q) => auditApi.suggestTableNames(q, 10)}
                            />
                        </div>
                    </div>

                    {/* Row 2: Date range + Search button */}
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[320px]">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Commit Time</label>
                            <div className="flex items-center gap-2">
                                <div className="flex-1">
                                    <div className="text-[10px] text-gray-400 mb-0.5 pl-1">From</div>
                                    <input
                                        type="date"
                                        value={filters.startDate}
                                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    />
                                </div>
                                <div className="pt-4">
                                    <span className="text-gray-400 text-xs">→</span>
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] text-gray-400 mb-0.5 pl-1">To</div>
                                    <input
                                        type="date"
                                        value={filters.endDate}
                                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 pb-0.5">
                            <button onClick={() => setDateRange('today')} className="px-3 py-2 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">Today</button>
                            <button onClick={() => setDateRange('7days')} className="px-3 py-2 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">7 Days</button>
                            <button onClick={() => setDateRange('30days')} className="px-3 py-2 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">30 Days</button>
                            {(filters.startDate || filters.endDate) && (
                                <button onClick={clearDateRange} className="px-3 py-2 text-xs rounded-md border border-red-200 text-red-500 hover:bg-red-50 transition-colors">Clear</button>
                            )}
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <span className="flex items-center gap-1.5">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Searching...
                                </span>
                            ) : 'Search'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Transaction Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-gray-500">Loading...</div>
                ) : (
                    <>
                        <div style={{ overflow: 'auto' }}>
                            <table className="min-w-max md:min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                                        {!selectedProject && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>}
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Function</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tables Affected</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Changes</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commit Time</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {transactions.map((tx) => (
                                        <tr key={tx.transactionId} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-mono text-gray-900 truncate" style={{ maxWidth: '220px' }}>
                                                    {tx.transactionId}
                                                </div>
                                            </td>
                                            {!selectedProject && (
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {tx.projectId || '-'}
                                                </td>
                                            )}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <SourceBadge source={tx.source} />
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 max-w-[150px] truncate">
                                                {tx.guiFunctionName || <span className="text-gray-400">-</span>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-1 max-w-[250px] max-h-6 overflow-hidden hover:max-h-32 hover:overflow-y-auto transition-all duration-200">
                                                    {tx.tablesAffected?.map((t, i) => (
                                                        <span key={i} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded whitespace-nowrap">
                                                            {t.table}
                                                        </span>
                                                    )) || <span className="text-gray-400 text-sm">-</span>}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-center">
                                                {tx.totalFieldChanges || 0}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                {formatTimestamp(tx.commitTimestamp)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <Link
                                                    to={`/transactions/${encodeURIComponent(tx.transactionId)}`}
                                                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                                >
                                                    View Detail →
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                    {transactions.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                                No transactions found. Try running some test SQL scenarios.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                                <div className="text-sm text-gray-700">
                                    Page {page + 1} of {totalPages}
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => loadTransactions(page - 1)}
                                        disabled={page === 0}
                                        className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => loadTransactions(page + 1)}
                                        disabled={page >= totalPages - 1}
                                        className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function SourceBadge({ source }) {
    const colors = {
        GUI: 'bg-purple-100 text-purple-800',
        SQL_DIRECT: 'bg-gray-100 text-gray-800',
    };
    return (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[source] || 'bg-gray-100 text-gray-600'}`}>
            {source || 'UNKNOWN'}
        </span>
    );
}

export default TransactionList;
